// =============================================================================
// Origin UI — src/components/compliance/FormRenderer.tsx
// Config-driven form engine (CM-7). Renders a form from a JSON field schema
// and validates client-side against the same rules as the CM-3 server
// validator. The renderer does NOT submit — it lifts validated answers up to
// the parent (DeclarationForm / AttestationFlow), which own the submit call.
//
// Schema is a BARE ARRAY of fields (confirmed against the seeded COC schema):
//   [ { key, label, type, required?, options?, maxDate?, ... }, ... ]
//
// Field types (locked in CM-3): ShortText, LongText, Number, Currency, Date,
// SingleSelect, MultiSelect, Confirmation, StaticText.
// =============================================================================

import { useMemo } from 'react';
import type {
  ComplianceFormField,
  ComplianceFieldType,
} from '@/services/complianceApi';
import styles from './FormRenderer.module.css';

// Answer value union. StaticText contributes no answer.
export type AnswerValue = string | number | boolean | string[] | null;
export type AnswerMap = Record<string, AnswerValue>;

interface FormRendererProps {
  /** Field schema (parsed from the version's FormSchema JSON string). */
  fields: ComplianceFormField[];
  /** Current answers, keyed by field key. Controlled by the parent. */
  values: AnswerMap;
  /** Called with the next answer map whenever a field changes. */
  onChange: (next: AnswerMap) => void;
  /**
   * Validation errors keyed by field key. The parent runs validateAnswers()
   * (exported below) on submit and passes the result here so messages show.
   */
  errors?: Record<string, string>;
  /** Disable all inputs (e.g. while a submit is in flight). */
  disabled?: boolean;
}

// ─── Helpers ─────────────────────────────────────────────────────────────

/** Resolve a min/max date literal: "today" → today's ISO date, else passthrough. */
function resolveDateLiteral(literal: string | undefined): string | undefined {
  if (!literal) return undefined;
  if (literal === 'today') {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }
  return literal;
}

/** Field types that hold a value (everything except StaticText). */
const INPUT_TYPES: ComplianceFieldType[] = [
  'ShortText',
  'LongText',
  'Number',
  'Currency',
  'Date',
  'SingleSelect',
  'MultiSelect',
  'Confirmation',
];

/** Whether a field is currently visible given showIf conditions. */
function isVisible(field: ComplianceFormField, values: AnswerMap): boolean {
  const showIf = (field as ComplianceFormField & {
    showIf?: { field: string; equals: string };
  }).showIf;
  if (!showIf) return true;
  return values[showIf.field] === showIf.equals;
}

// ─── Validation (mirrors the CM-3 server validator) ───────────────────────

/**
 * Validate answers against the schema. Returns a map of field key → message.
 * Empty map means valid. Hidden (showIf) and StaticText fields are skipped.
 */
export function validateAnswers(
  fields: ComplianceFormField[],
  values: AnswerMap,
): Record<string, string> {
  const errors: Record<string, string> = {};

  for (const field of fields) {
    if (field.type === 'StaticText') continue;
    if (!isVisible(field, values)) continue;

    const value = values[field.key];

    // ── Confirmation: required means must be ticked ──
    if (field.type === 'Confirmation') {
      if (field.required && value !== true) {
        errors[field.key] = 'This must be confirmed.';
      }
      continue;
    }

    // ── MultiSelect ──
    if (field.type === 'MultiSelect') {
      const arr = Array.isArray(value) ? value : [];
      if (field.required && arr.length === 0) {
        errors[field.key] = 'Please select at least one option.';
      }
      continue;
    }

    // ── Empty check (everything else) ──
    const isEmpty =
      value === null ||
      value === undefined ||
      (typeof value === 'string' && value.trim() === '');

    if (isEmpty) {
      if (field.required) errors[field.key] = 'This field is required.';
      continue; // nothing more to validate on an empty optional field
    }

    // ── Type-specific checks ──
    if (field.type === 'ShortText' || field.type === 'LongText') {
      if (
        typeof field.maxLength === 'number' &&
        String(value).length > field.maxLength
      ) {
        errors[field.key] = `Must be ${field.maxLength} characters or fewer.`;
      }
    } else if (field.type === 'Number' || field.type === 'Currency') {
      const num = typeof value === 'number' ? value : Number(value);
      if (Number.isNaN(num)) {
        errors[field.key] = 'Please enter a valid number.';
      } else {
        if (typeof field.min === 'number' && num < field.min) {
          errors[field.key] = `Must be at least ${field.min}.`;
        } else if (typeof field.max === 'number' && num > field.max) {
          errors[field.key] = `Must be at most ${field.max}.`;
        }
      }
    } else if (field.type === 'Date') {
      const minDate = resolveDateLiteral(field.minDate);
      const maxDate = resolveDateLiteral(field.maxDate);
      const dateStr = String(value);
      if (minDate && dateStr < minDate) {
        errors[field.key] = `Date cannot be before ${minDate}.`;
      } else if (maxDate && dateStr > maxDate) {
        errors[field.key] =
          maxDate === resolveDateLiteral('today')
            ? 'Date cannot be in the future.'
            : `Date cannot be after ${maxDate}.`;
      }
    } else if (field.type === 'SingleSelect') {
      const allowed = (field.options ?? []).map((o) => o.value);
      if (allowed.length > 0 && !allowed.includes(String(value))) {
        errors[field.key] = 'Please choose a valid option.';
      }
    }
  }

  return errors;
}

/**
 * Strip an answer map down to only the fields that should be submitted:
 * excludes StaticText and any currently-hidden (showIf) fields.
 */
export function collectAnswers(
  fields: ComplianceFormField[],
  values: AnswerMap,
): AnswerMap {
  const out: AnswerMap = {};
  for (const field of fields) {
    if (!INPUT_TYPES.includes(field.type)) continue;
    if (!isVisible(field, values)) continue;
    if (field.key in values) out[field.key] = values[field.key];
  }
  return out;
}

// ─── Component ─────────────────────────────────────────────────────────────

export function FormRenderer({
  fields,
  values,
  onChange,
  errors = {},
  disabled = false,
}: FormRendererProps) {
  const visibleFields = useMemo(
    () => fields.filter((f) => isVisible(f, values)),
    [fields, values],
  );

  function setValue(key: string, value: AnswerValue) {
    onChange({ ...values, [key]: value });
  }

  function toggleMulti(key: string, optionValue: string, checked: boolean) {
    const current = Array.isArray(values[key]) ? (values[key] as string[]) : [];
    const next = checked
      ? [...current, optionValue]
      : current.filter((v) => v !== optionValue);
    setValue(key, next);
  }

  return (
    <div className={styles.form}>
      {visibleFields.map((field) => {
        const error = errors[field.key];
        const value = values[field.key];

        // ── StaticText: guidance only ──
        if (field.type === 'StaticText') {
          return (
            <p key={field.key} className={styles.staticText}>
              {field.label}
            </p>
          );
        }

        // ── Confirmation: single tick ──
        if (field.type === 'Confirmation') {
          return (
            <div key={field.key} className={styles.field}>
              <label className={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={value === true}
                  disabled={disabled}
                  onChange={(e) => setValue(field.key, e.target.checked)}
                />
                <span className={styles.checkboxLabel}>
                  {field.label}
                  {field.required && <span className={styles.req}> *</span>}
                </span>
              </label>
              {field.helpText && (
                <p className={styles.helpText}>{field.helpText}</p>
              )}
              {error && <p className={styles.error}>{error}</p>}
            </div>
          );
        }

        // ── Everything else has a label + control ──
        return (
          <div key={field.key} className={styles.field}>
            <label className={styles.label} htmlFor={`cf_${field.key}`}>
              {field.label}
              {field.required && <span className={styles.req}> *</span>}
            </label>
            {field.helpText && (
              <p className={styles.helpText}>{field.helpText}</p>
            )}

            {field.type === 'ShortText' && (
              <input
                id={`cf_${field.key}`}
                type="text"
                className={styles.input}
                value={typeof value === 'string' ? value : ''}
                maxLength={field.maxLength}
                disabled={disabled}
                onChange={(e) => setValue(field.key, e.target.value)}
              />
            )}

            {field.type === 'LongText' && (
              <textarea
                id={`cf_${field.key}`}
                className={styles.textarea}
                rows={4}
                value={typeof value === 'string' ? value : ''}
                maxLength={field.maxLength}
                disabled={disabled}
                onChange={(e) => setValue(field.key, e.target.value)}
              />
            )}

            {(field.type === 'Number' || field.type === 'Currency') && (
              <div className={styles.numberWrap}>
                {field.type === 'Currency' && (
                  <span className={styles.currencyPrefix}>USD</span>
                )}
                <input
                  id={`cf_${field.key}`}
                  type="number"
                  className={`${styles.input} ${
                    field.type === 'Currency' ? styles.currencyInput : ''
                  }`}
                  value={
                    typeof value === 'number'
                      ? value
                      : typeof value === 'string'
                      ? value
                      : ''
                  }
                  min={field.min}
                  max={field.max}
                  step={
                    typeof field.decimalPlaces === 'number'
                      ? Math.pow(10, -field.decimalPlaces)
                      : 'any'
                  }
                  disabled={disabled}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setValue(field.key, raw === '' ? null : Number(raw));
                  }}
                />
              </div>
            )}

            {field.type === 'Date' && (
              <input
                id={`cf_${field.key}`}
                type="date"
                className={styles.input}
                value={typeof value === 'string' ? value : ''}
                min={resolveDateLiteral(field.minDate)}
                max={resolveDateLiteral(field.maxDate)}
                disabled={disabled}
                onChange={(e) => setValue(field.key, e.target.value)}
              />
            )}

            {field.type === 'SingleSelect' && (
              <div className={styles.radioGroup}>
                {(field.options ?? []).map((opt) => (
                  <label key={opt.value} className={styles.radioRow}>
                    <input
                      type="radio"
                      name={`cf_${field.key}`}
                      value={opt.value}
                      checked={value === opt.value}
                      disabled={disabled}
                      onChange={() => setValue(field.key, opt.value)}
                    />
                    <span>{opt.label}</span>
                  </label>
                ))}
              </div>
            )}

            {field.type === 'MultiSelect' && (
              <div className={styles.radioGroup}>
                {(field.options ?? []).map((opt) => {
                  const arr = Array.isArray(value) ? (value as string[]) : [];
                  return (
                    <label key={opt.value} className={styles.radioRow}>
                      <input
                        type="checkbox"
                        value={opt.value}
                        checked={arr.includes(opt.value)}
                        disabled={disabled}
                        onChange={(e) =>
                          toggleMulti(field.key, opt.value, e.target.checked)
                        }
                      />
                      <span>{opt.label}</span>
                    </label>
                  );
                })}
              </div>
            )}

            {error && <p className={styles.error}>{error}</p>}
          </div>
        );
      })}
    </div>
  );
}
